<template>
  <div class="inv-upload">

    <!-- Drop Zone -->
    <div
      class="inv-dropzone"
      :class="{
        'inv-dropzone--active': isDragging,
        'inv-dropzone--error': hasError
      }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
      @click="$refs.fileInput.click()"
    >
      <input
        ref="fileInput"
        type="file"
        :multiple="multiple"
        :accept="accept"
        class="inv-dropzone__input"
        @change="onFileChange"
      />

      <div class="inv-dropzone__inner">
        <div class="inv-dropzone__icon">
          <span v-if="isDragging">↓</span>
          <span v-else>⊕</span>
        </div>
        <div class="inv-dropzone__label">
          <span v-if="isDragging">Drop the file here</span>
          <span v-else>
            File upload
            <span class="inv-dropzone__sub">or drag and drop</span>
          </span>
        </div>
        <div class="inv-dropzone__hint">
          {{ acceptLabel }} &nbsp;·&nbsp; max. {{ maxSizeMb }} MB / file
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="hasError" class="inv-upload-error">
      <span class="inv-upload-error__icon">⚠</span>
      {{ errorMessage }}
    </div>

    <!-- File List -->
    <div v-if="files.length" class="inv-filelist">
      <div class="inv-filelist__header">
        <span class="inv-filelist__title">ATTACHED FILES</span>
        <span class="inv-filelist__count">{{ files.length }}</span>
      </div>

      <div
        v-for="(file, index) in files"
        :key="index"
        class="inv-file"
        :class="{ 'inv-file--uploading': file.status === 'uploading', 'inv-file--done': file.status === 'done', 'inv-file--error': file.status === 'error' }"
      >
        <!-- Icon -->
        <div class="inv-file__icon">{{ fileGlyph(file.name) }}</div>

        <!-- Info -->
        <div class="inv-file__info">
          <div class="inv-file__name">{{ file.name }}</div>
          <div class="inv-file__meta">
            {{ formatSize(file.size) }}
            <span v-if="file.status === 'uploading'"> &nbsp;·&nbsp; uploading...</span>
            <span v-else-if="file.status === 'done'" class="inv-file__status inv-file__status--done"> &nbsp;·&nbsp; ✓ DONE</span>
            <span v-else-if="file.status === 'error'" class="inv-file__status inv-file__status--error"> &nbsp;·&nbsp; ✕ ERROR</span>
            <span v-else> &nbsp;·&nbsp; <span class="inv-file__status inv-file__status--pending">PENDING</span></span>
          </div>
          <!-- Progress bar -->
          <div v-if="file.status === 'uploading'" class="inv-file__progress">
            <div class="inv-file__progress-bar" :style="{ width: file.progress + '%' }"></div>
          </div>

            <div class="inv-file__error-msg" v-if="file.status === 'error' && file.errorMessage">{{ file.errorMessage }}</div>
        </div>

        <!-- Remove -->
        <button
          v-if="file.status !== 'uploading'"
          class="inv-file__remove"
          title="Remove file"
          @click.stop="removeFile(index)"
        >✕</button>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'ToolsFileUpload',

  props: {
    multiple:     { type: Boolean, default: true },
    accept:       { type: String,  default: '*/*' },
    acceptLabel:  { type: String,  default: 'All file types' },
    maxSizeMb:    { type: Number,  default: 10 },
  },

  data() {
    return {
      isDragging: false,
      files: [],
      hasError: false,
      errorMessage: '',
    }
  },

  computed: {
    pendingCount() {
      return this.files.filter(f => f.status === 'pending').length
    },
  },

  methods: {
    onDrop(e) {
      this.isDragging = false
      const droppedFiles = Array.from(e.dataTransfer.files)
      this.addFiles(droppedFiles)
    },
    onFileChange(e) {
      this.addFiles(Array.from(e.target.files))
      e.target.value = ''
    },
    addFiles(rawFiles) {
      this.hasError = false
      const maxBytes = this.maxSizeMb * 1024 * 1024
      const oversized = rawFiles.filter(f => f.size > maxBytes)
      if (oversized.length) {
        this.hasError = true
        this.errorMessage = `${oversized.map(f => f.name).join(', ')} — size exceeds the ${this.maxSizeMb} MB limit`
        return
      }
      const newFiles = rawFiles.map(f => ({
        name: f.name,
        size: f.size,
        type: f.type,
        raw: f,
        status: 'pending', // pending | uploading | done | error
        progress: 0,
      }))
      if (this.multiple) {
        this.files.push(...newFiles)
      } else {
        this.files = newFiles.slice(0, 1)
      }
      this.$emit('files-selected', newFiles)
    },
    removeFile(index) {
      const removed = this.files.splice(index, 1)
      this.$emit('remove', removed[0])
    },
    clearAll() {
      this.files = []
      this.$emit('clear')
    },

    fileGlyph(name) {
      const ext = (name.split('.').pop() || '').toLowerCase()
      const map = {
        pdf: '▤', jpg: '▨', jpeg: '▨', png: '▨', gif: '▨', webp: '▨',
        doc: '▦', docx: '▦', xls: '▦', xlsx: '▦',
        zip: '▣', rar: '▣', '7z': '▣',
        mp4: '▶', mov: '▶', avi: '▶',
      }
      return map[ext] || '▢'
    },
    formatSize(bytes) {
      if (bytes < 1024)       return bytes + ' B'
      if (bytes < 1048576)    return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&display=swap');

.inv-upload {
  --bg:        #ffffff;
  --bg2:       #f5f6f8;
  --bg3:       #f0f1f4;
  --border:    #e2e4ea;
  --border2:   #c8ccd6;
  --text:      #374151;
  --text-dim:  #9ca3af;
  --text-bright: #111827;
  --accent:    #2563eb;
  --accent2:   #1d4ed8;
  --danger:    #ef4444;
  --warn:      #f59e0b;
  --success:   #16a34a;

  font-family: 'IBM Plex Sans', sans-serif;
  color: var(--text);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── DROP ZONE ── */
.inv-dropzone {
  border: 1.5px dashed var(--border2);
  background: var(--bg2);
  padding: 36px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  position: relative;
}
.inv-dropzone:hover {
  border-color: var(--accent);
  background: #eff6ff;
}
.inv-dropzone--active {
  border-color: var(--accent);
  background: #eff6ff;
  border-style: solid;
}
.inv-dropzone--error {
  border-color: var(--danger);
  background: #fef2f2;
}
.inv-dropzone__input {
  display: none;
}
.inv-dropzone__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
}
.inv-dropzone__icon {
  font-size: 28px;
  color: var(--accent);
  font-family: 'IBM Plex Mono', monospace;
  line-height: 1;
  transition: transform 0.15s;
}
.inv-dropzone--active .inv-dropzone__icon {
  transform: translateY(4px);
}
.inv-dropzone__label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--text-bright);
  display: flex;
  align-items: center;
  gap: 10px;
}
.inv-dropzone__sub {
  font-weight: 400;
  color: var(--text-dim);
  letter-spacing: 0.04em;
}
.inv-dropzone__hint {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: var(--text-dim);
  letter-spacing: 0.06em;
}

/* ── ERROR ── */
.inv-upload-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1px solid var(--danger);
  background: #fef2f2;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  color: var(--danger);
  letter-spacing: 0.04em;
}
.inv-upload-error__icon { font-size: 13px; }

/* ── FILE LIST ── */
.inv-filelist {
  border: 1px solid var(--border);
  background: var(--bg);
}
.inv-filelist__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--bg3);
}
.inv-filelist__title {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: var(--text-dim);
}
.inv-filelist__count {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  color: var(--accent);
  background: rgba(37,99,235,0.08);
  border: 1px solid rgba(37,99,235,0.2);
  padding: 1px 7px;
}

.inv-file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  transition: background 0.1s;
}
.inv-file:last-child { border-bottom: none; }
.inv-file:hover { background: var(--bg2); }
.inv-file--done  { background: #f0fdf4; }
.inv-file--error { background: #fef2f2; }

.inv-file__icon {
  font-size: 18px;
  color: var(--text-dim);
  flex-shrink: 0;
  font-family: 'IBM Plex Mono', monospace;
  width: 20px;
  text-align: center;
}
.inv-file--done  .inv-file__icon { color: var(--success); }
.inv-file--error .inv-file__icon { color: var(--danger); }

.inv-file__info {
  flex: 1;
  min-width: 0;
}
.inv-file__name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-bright);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.inv-file__meta {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: var(--text-dim);
  letter-spacing: 0.04em;
  margin-top: 2px;
}
.inv-file__status--done    { color: var(--success); font-weight: 700; }
.inv-file__status--error   { color: var(--danger);  font-weight: 700; }
.inv-file__status--pending { color: var(--warn); }

.inv-file__progress {
  height: 2px;
  background: var(--border);
  margin-top: 6px;
  overflow: hidden;
}
.inv-file__progress-bar {
  height: 100%;
  background: var(--accent);
  transition: width 0.15s ease;
}

.inv-file__remove {
  background: none;
  border: 1px solid transparent;
  color: var(--text-dim);
  font-size: 13px;
  cursor: pointer;
  padding: 3px 7px;
  font-family: 'IBM Plex Mono', monospace;
  transition: all 0.12s;
  flex-shrink: 0;
}
.inv-file__remove:hover {
  border-color: var(--danger);
  color: var(--danger);
  background: rgba(239,68,68,0.06);
}

/* ── ACTIONS ── */
.inv-upload-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}
.inv-upload-summary {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  color: var(--text-dim);
  letter-spacing: 0.06em;
  margin-right: auto;
}

/* ── SHARED BUTTONS (same as datatable) ── */
.inv-btn {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  padding: 8px 18px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.inv-btn--primary { background: var(--accent); border-color: var(--accent); color: #fff; }
.inv-btn--primary:hover:not(:disabled) { background: var(--accent2); border-color: var(--accent2); }
.inv-btn--primary:disabled { opacity: 0.4; cursor: default; }
.inv-btn--ghost   { background: transparent; border-color: var(--border2); color: var(--text); }
.inv-btn--ghost:hover { border-color: var(--text-dim); }
.inv-file__error-msg {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  color: var(--danger);
  margin-top: 3px;
  letter-spacing: 0.03em;
}
</style>